/**
 * Created by Ella Ma on 2020/09/10.
 * ------ maintenance history ------
 * Changed by Daniel on 3/3/2021 - Changed the style and logic of setting menu
 * Changed by Daniel on 3/6/2021 - Added draft mode
 * Changed by Daniel on 3/9/2021 - Organized file structure and added more comments
 * Changed by Daniel on 3/23/2021 - Added export and import feature
 */

import { Component, OnInit, HostListener, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, map, skip, switchMap, take } from 'rxjs/operators';
import { AppState } from '../../../redux';
import * as _lodash from 'lodash';

import { currentWorkflowFieldsSelector, workflowBriefSelector, workflowFieldsSelector, workflowMapSelector } from '../../../tamalelibs/redux/reducers/workflow.reducer';
import { WorkflowEditorSubChromeConfig, WorkflowEditorSubChromeActionType, WorkflowEditorSubChromeEventType } from './workflow-config-sub-chrome/workflow-config-sub-chrome.model';
import { WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { FieldBrief, FieldDefinition, GatewayType, ProcessDefinition, TaskDefinition, TaskDefinitionOpType, TaskType, WorkflowConfig, TaskTypes, GatewayTypes, TaskSubType, GatewayDefinition, ProcessInstanceStatus, FieldDefinitionConfiguration, ProcessDefinitionStatus, TaskColor, PostNotification, COLORS, Field, Parent } from '../../../tamalelibs/models/workflow.model';
import { ToastService } from '../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { DiagramConfig, NodeActionType, NodeEventType, START_NODE } from '../../../widgets/diagram/diagram.model';
import { DiagramService } from '../../../widgets/diagram/diagram.service';
import { WorkflowDialogService } from '../workflow-dialog/workflow-dialog.service';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { TaskDefinitionEventTypes } from '../../../components/work-flow/task-definition/task-definition.model';
import { DefaultGatewayInfo, WorkflowDialogOperations } from '../workflow-model';
import { SystemActionTypes } from '../../../tamalelibs/redux/actions/system.actions';
import { FloatMenuService } from '../../../services/float-menu.service';
import { WorkflowConfigService } from './../../../tamalelibs/services/workflow-config.service';
import { screensSelector } from '../../../redux/reducers/system.reducer';
import { businessConstants } from '../../../constants/business.constants';
import { ScreenService } from '../../../tamalelibs/services/screen.service';
import { WorkflowTriggerSettingsEventTypes } from '../../../components/work-flow/workflow-trigger-settings/workflow-trigger-settings.model';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { ControlType } from '../../../tamalelibs/models/template-control.model';
import { DefinitionSetting, WorkflowTriggerSettings } from '../../../tamalelibs/models/workflow-config.model';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { AutomationActionType, TaskMenuItem } from '../../../widgets/diagram/diagram-context-menu/diagram-context-menu.model';
import { usersAndTeamsSelector } from '../../../redux/reducers/users.reducer';
import { fullEntityTypeSelector } from '../../../redux/reducers/entity-type.reducer';
import { relationshipTypeSelector } from '../../../redux/reducers/relationship-type.reducer';
import { EntityService } from '../../../tamalelibs/services/entity.service';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { DiagramBranch } from '../../../widgets/diagram/diagram-service';
import { WorkflowConfigUtility } from './workflow-config.utilities';
import { DiagramAutomationNoteDialogService } from '../../../widgets/diagram/diagram-automation-dialog/diagram-note-automation-dialog/diagram-note-automation-dialog.service';
import { EntryType } from '../../../tamalelibs/models/entry-type.model';

@Component({
    selector: 'tam-workflow-config',
    templateUrl: './workflow-config.component.html',
    styleUrls: ['./workflow-config.component.scss']
})

export class WorkflowConfigComponent implements OnInit, OnDestroy {
    config: WorkflowConfig;
    contextMenuConfig: ContextMenuConfig;
    diagramConfig = new DiagramConfig();
    showDiagram = false;
    workflowEditorSubChromeConfig = new WorkflowEditorSubChromeConfig();

    private _activeTask;
    private _colors = _lodash.cloneDeep(COLORS);
    private _defaultConfig: WorkflowConfig; // default workflow config
    private _defaultTaskDefinition: Map<string, TaskDefinition> = new Map(); // default task definition
    private _definitionSetting = new DefinitionSetting();
    private _destroySubscriptions: Subscription[] = [];
    private _hasChangedGatewaySet: Set<string> = new Set(); // has changed gateway
    private _hasChangedTaskDefinitionSet: Set<string> = new Set(); // has change task definition
    private _gatewayMap: Map<string, GatewayDefinition> = new Map();
    private _isImportWorkflow = false;
    private _opType: string;
    private _screenId: string;
    private _taskDefinitionMap: Map<string, TaskDefinition> = new Map(); // all valid task definition
    private _taskSlideSheetForTaskDefinitionAction$ = new Subject();
    private _taskSlideSheetForTaskDefinitionEvent$ = new Subject();
    private _triggerCondition = new WorkflowTriggerSettings();
    private _triggerSettingSlideSheet$ = new Subject();

    constructor(
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _diagramAutomationDialogService: DiagramAutomationNoteDialogService,
        private _diagramService: DiagramService,
        private _entityService: EntityService,
        private _floatMenuService: FloatMenuService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _store: Store<AppState>,
        private _toast: ToastService,
        private _screenService: ScreenService,
        private _slideSheetService: SlideSheetService,
        private _storeQueriery: StoreQuerierService,
        private _workflowConfigService: WorkflowConfigService,
        private _workflowDialogService: WorkflowDialogService,
        private _workflowService: WorkflowService,
    ) { }

    ngOnInit(): void {
        // get ids (8 bit) from server
        this._store.dispatch({
            type: WorkflowActionTypes.GET_IDS
        });
        // hide float-menu-draft-count
        this._floatMenuService.floatMenuBottomSubject$.next(false);
        this._initContextMenu();
        // init fields
        const fields: Array<FieldDefinition> = this._storeQueriery.queryBySelector(workflowFieldsSelector);
        if (!fields) {
            this._store.dispatch({
                type: WorkflowActionTypes.GET_ALL_FIELDS,
            });
        }

        // get operation type by url
        this._opType = this._route.snapshot.url[2].path.toUpperCase();
        this._screenId = this._route.snapshot.params.id;

        // set status by operation type
        this.workflowEditorSubChromeConfig.opType = this._opType;
        if (this._opType.toUpperCase() === WorkflowDialogOperations.EDIT && !this._screenId) {
            this._isImportWorkflow = true;
        }
        // tirgger special logic for import workflow
        if (this._isImportWorkflow) {
            // import workflow from file
            this._initComponentByImport();
        } else {
            // init component (new/edit/draft)
            this._initComponent();
            // change different workflow by navigate menu (URL)
            this._route.paramMap.pipe(
                skip(1)
            ).subscribe((router) => {
                this._store.dispatch({
                    type: WorkflowActionTypes.CLEAR_CURRENT_WORKFLOW_TASK,
                });
                this._store.dispatch({
                    type: WorkflowActionTypes.CLEAR_AUTOMATION_NODE,
                });
                // used for the Guard
                this.workflowEditorSubChromeConfig.changeRouterBySelf = true;
                this._opType = WorkflowDialogOperations.DRAFT;
                this.workflowEditorSubChromeConfig.opType = this._opType;
                this._screenId = router['params'].id;
                this._initComponent();
                this.diagramConfig.actions$.next({
                    type: NodeActionType.refresh
                });
            });
        }

        // register the events
        this._destroySubscriptions.push(
            this.workflowEditorSubChromeConfig.actions$.subscribe((action) => this._workflowEditorSubChromeHandlers(action)),
            // handle diagram behavior
            this.diagramConfig.events$.subscribe(events => this._diagramHandlers(events)),
            // handle stage behavior
            this._workflowService.workflowActionSubject$.subscribe(action => {
                if (action.type === WorkflowActionTypes.DELETE_STAGE) {
                    const stageId: string = action.payload.id;
                    this._taskDefinitionMap.forEach((value, key) => {
                        if (value.stage && value.stage.id === stageId) {
                            delete value.stage;
                            delete value.stageColor;
                            this._diagramService.updateNode(key, { valid: false, stage: '' });
                        }
                    });
                    this._store.dispatch({
                        type: WorkflowActionTypes.DELETE_STAGE_SUCCESS,
                        payload: action.payload,
                    });
                }
            }),
            // handle slide sheet behavior
            this._taskSlideSheetForTaskDefinitionEvent$.pipe(
                filter((action: any) => action)
            ).subscribe(this._actionHandler.bind(this)),
            // handle trigger settings behavior
            this._triggerSettingSlideSheet$.subscribe((action: any) => {
                if (action.type === WorkflowTriggerSettingsEventTypes.SAVE) {
                    // get workflow trigger setting from service
                    let triggerSetting = action.payload.triggerSetting;
                    if (!triggerSetting) {
                        triggerSetting = new WorkflowTriggerSettings();
                    }
                    this._triggerCondition = triggerSetting;
                    // set workflow trigger setting for setting the correct status for creating sidenote
                    this._workflowConfigService.setWorkflowTriggerSettingConfig = _lodash.cloneDeep(triggerSetting);
                    if (action.payload.definitionSetting && action.payload.definitionSetting.primaryEntity) {
                        delete action.payload.definitionSetting.primaryEntity.fieldDefEntityName;
                    }
                    this._definitionSetting = action.payload.definitionSetting;
                    this.diagramConfig.invalidSetting = action.payload.invalidSetting;

                    this.workflowEditorSubChromeConfig.changeRouterBySelf = false;

                    // udpate the parameter of automation nodes
                    this.diagramConfig.nodes = this._diagramService.serialize();
                    this.diagramConfig.nodes.forEach(node => {
                        const automationItem = node['automation'];
                        if (automationItem && automationItem.length > 0) {
                            automationItem.forEach(item => {
                                if (item.action === 'update') {
                                    // override the entry type id in update mode.
                                    item.objectTypeID = triggerSetting.triggerSource[0].objectTypeID;
                                    // update automation name if the entry type name was changed
                                    this._updatedAutomationNameByEntryType(item, triggerSetting);
                                }
                            });
                        }
                        // handle the conditional gateway
                        if (node.branchs && node.branchs.length > 0) {
                            node.branchs.forEach(branch => {
                                const automationConfig = branch['automationConfigs'];
                                if (automationConfig && automationConfig.length > 0) {
                                    automationConfig.forEach(item => {
                                        if (item.action === 'update') {
                                            // override the entry type id in update mode.
                                            item.objectTypeID = triggerSetting.triggerSource[0].objectTypeID;
                                            // update automation name if the entry type name was changed
                                            this._updatedAutomationNameByEntryType(item, triggerSetting);
                                        }
                                    });
                                }
                            });
                        }
                    });
                }
            }),
            // get data from store to init parameters
            this._store.pipe(
                select(workflowBriefSelector),
            ).subscribe(config => {
                if (config) {
                    this._initData(config);
                }

                if (this._isImportWorkflow) {
                    this._validImportWorkflowLogic().pipe(take(1)).subscribe(() => {
                        this._setSaveButtonLoading(false);
                    });
                }
            }),

            this._workflowDialogService.edit$.subscribe(config => {
                this._defaultConfig = config;
            }),

            // import workflow listener to solve the problem of duplicate imports
            this._workflowDialogService.import$.subscribe(() => {
                this._initComponentByImport();
                this._validImportWorkflowLogic().pipe(take(1)).subscribe(() => {
                    this._setSaveButtonLoading(false);
                });

                this.diagramConfig.actions$.next({
                    type: NodeActionType.refresh
                });
            }),

            this._route.queryParams.subscribe(params => {
                const _isChangeRouterBySelf = params['isChangeRouterBySelf'];
                if (_isChangeRouterBySelf) {
                    this.workflowEditorSubChromeConfig.changeRouterBySelf = _isChangeRouterBySelf;
                }
            })
        );
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(item => item && item.unsubscribe());
        // edit workflow -> create new workflow -> click dialog save button, router change and component destory, but should not clear workflow brief
        if (!this.workflowEditorSubChromeConfig.changeRouterBySelfDialog || this._isImportWorkflow) {
            this._store.dispatch({
                type: WorkflowActionTypes.CLEAR_WORKFLOW_BRIEF
            });
        }
        this._resetPageData();
        this._floatMenuService.floatMenuBottomSubject$.next(true);
    }

    onDiagramScroll(event) {
        if (this.diagramConfig) {
            this.diagramConfig.actions$.next({
                type: NodeActionType.closeContextMenu
            });
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any) {
        $event.returnValue = true;
    }

    private _actionHandler(action) {
        if (action.type === TaskDefinitionEventTypes.SAVE) {
            // update the data after task definition was saved
            this._hasChangedTaskDefinitionSet.add(this._activeTask.id);
            const taskDefinition = action.payload.data;
            this._taskDefinitionMap.set(this._activeTask.id, taskDefinition);
            this._diagramService.setNodeId(this._activeTask.guid, taskDefinition.id);
            const taskStageColor = this._setColorForStages(taskDefinition.stageColor);
            this._diagramService.updateNode(this._activeTask.guid, { name: taskDefinition.name, stage: taskDefinition.stage ? taskDefinition.stage.id : '', stageColor: taskStageColor, valid: action.payload.valid });
            // update the post action status for each task
            this._validTaskPostActionsByTemplateEntityFieldsChange();
            this.diagramConfig.nodes = this._diagramService.serialize();
            const _taskMenuItem = new TaskMenuItem(taskDefinition.id, taskDefinition.guid, taskDefinition.name);
            this._store.dispatch({
                type: WorkflowActionTypes.UPDATE_CURRENT_WORKFLOW_TASK,
                payload: _taskMenuItem
            });

            this.workflowEditorSubChromeConfig.changeRouterBySelf = false;
        } else if (action.type === TaskDefinitionEventTypes.ERROR) {
            // TODO, when completed task-definition configration, update the code, now default behavior is delete node
            this._taskDefinitionMap.delete(this._activeTask.id);
            this._diagramService.deleteNode(this._activeTask.guid);
            this.diagramConfig.nodes = this._diagramService.serialize();
            this._activeTask = null;
        }
    }

    private _addAutomationNodesToStore(nodes): void {
        let noteTypes = Array<EntryType>();
        this._diagramAutomationDialogService.getNoteTypes().pipe(take(1)).subscribe(res => {
            noteTypes = Array.from(res.entryTypes);
        });
        // add the automation nodes to store
        if (nodes && nodes.length > 0) {
            const automationNodes = [];
            nodes.forEach(node => {
                // add the automation config on the nodes to the container
                if (node.automationConfigs && node.automationConfigs.length > 0) {
                    node.automationConfigs.forEach(config => {
                        config.enable = true;
                        config.name = this._workflowService.getAutomationCardContent(config, noteTypes);
                        config.attachedNodeId = node.id ? node.id : node.guid;
                    });

                    automationNodes.push(...node.automationConfigs);
                }
                // add the automation config on the conditional branch to the container
                if (node.branchs && node.branchs.length > 0) {
                    node.branchs.forEach(branch => {
                        if (branch.automationConfigs && branch.automationConfigs.length > 0) {
                            branch.automationConfigs.forEach(config => {
                                config.enable = true;
                                config.name = this._workflowService.getAutomationCardContent(config, noteTypes);
                                config.attachedNodeId = branch.id ? branch.id : branch.guid;
                            });

                            automationNodes.push(...branch.automationConfigs);
                        }
                    });
                }
            });
            if (automationNodes.length > 0) {
                this._store.dispatch({
                    type: WorkflowActionTypes.ADD_AUTOMATION_NODE,
                    payload: automationNodes
                });
            }
        }
    }

    private _changeRouter() {
        this.workflowEditorSubChromeConfig.changeRouterBySelf = true;
        const _currentOperationType = this._route.snapshot.url[2].path.toUpperCase();
        this._router.navigate(['workflow/config/draft', this.config.id], { queryParams: { isChangeRouterBySelf: true } });
        this._opType = WorkflowDialogOperations.DRAFT;
        this.workflowEditorSubChromeConfig.opType = this._opType;
        this._screenId = this.config.id;
        if (_currentOperationType === this._opType) {
            this._initComponent();
        }
        this.diagramConfig.actions$.next({
            type: NodeActionType.refresh
        });
    }

    private _createGateway(gatewayType: GatewayType) {
        const data = [];
        const gateWaySeries: Array<any> = DefaultGatewayInfo[gatewayType.toString()];
        if (gateWaySeries && gateWaySeries.length > 0) {
            let precursor: GatewayDefinition, succeed: GatewayDefinition;
            gateWaySeries.forEach(item => {
                const gateway = new GatewayDefinition();
                gateway.id = this._workflowService.getIdFromServerInStore(this._store);
                gateway.name = item.name;
                gateway.type = item.type;
                // save gateway in local
                this._gatewayMap.set(gateway.id, gateway);
                this._hasChangedGatewaySet.add(gateway.id);
                if (!precursor) {
                    precursor = gateway;
                } else if (!succeed) {
                    succeed = gateway;
                }

                data.push(gateway);
            });

            if (precursor && succeed) {
                // set the pointer of the precursor and the succeed for new gateway nodes.
                precursor.target = succeed.id;
                succeed.source = precursor.id;
            }

            // update diagram node
            this._diagramService.updateNode(this._activeTask.guid, { gatewayConfig: data });
            this.diagramConfig.nodes = this._diagramService.serialize();
        }
    }

    private _createWorkflowBrief(definitions?: any): void {
        // format diagram nodes configuration for saving
        const data = this._formatConfiguration();
        this.config.configuration.connectionLine = data.connectionLine;
        this.config.configuration.startTask = data.startTask;
        this.config.configuration.triggerCondition = data.triggerCondition;
        this.config.configuration.definitionSetting = data.definitionSetting;
        this.config.configuration.itemDefinition = definitions;

        if (!this.config.configuration.status) {
            const status = new ProcessDefinitionStatus();
            status[ProcessInstanceStatus.REJECTED] = ['Rejected'];
            status[ProcessInstanceStatus.IN_PROCESS] = ['In Progress'];
            status[ProcessInstanceStatus.DONE] = ['Done'];
            this.config.configuration.status = status;
        }
        // delete useless properties
        delete this.config.configuration.draftConfig;
        delete this.config.configuration.draft;
        const oldStr = JSON.stringify(this._defaultConfig);
        const newStr = JSON.stringify(this.config);
        let notModifyFilter = false;
        if (oldStr && newStr) {
            notModifyFilter = _lodash.isEqual(JSON.parse(oldStr), JSON.parse(newStr));
        }
        if (notModifyFilter && !this._isImportWorkflow) {
            this._successHandler(this.config);
            return;
        }
        let opType = this._opType;
        if (this._opType === WorkflowDialogOperations.DRAFT) {
            if (this.config.id && !this._isImportWorkflow) {
                opType = WorkflowDialogOperations.EDIT;
            } else {
                opType = WorkflowDialogOperations.NEW;
            }
        }
        if (this._isImportWorkflow) {
            this._opType = opType = WorkflowDialogOperations.NEW;
        }

        if (opType === WorkflowDialogOperations.NEW) {
            delete this.config.id;
            this._workflowService.createWorkflowBrief(this.config).pipe(
                take(1),
            ).subscribe((config) => this._successHandler(config), error => this._errorHandler(error));
        } else if (opType === WorkflowDialogOperations.EDIT) {
            this._workflowService.updateWorkflowBrief(this.config).pipe(
                take(1),
            ).subscribe((config) => this._successHandler(config), error => this._errorHandler(error));
        }
    }

    private _deleteNode() {
        this._diagramService.deleteNode(this._activeTask.guid);
        this._removeFromMapAndStore(this._activeTask.id);
        this._activeTask = null;
    }

    private _diagramHandlers(event): void {
        if (event.type === NodeEventType.enterNode) {
            this.diagramConfig.nodes = this._diagramService.serialize();
            // add the task to store
            if (event.payload.name) {
                const taskMenuItem = new TaskMenuItem(event.payload.id, event.payload.guid, event.payload.name);
                this._store.dispatch({
                    type: WorkflowActionTypes.UPDATE_CURRENT_WORKFLOW_TASK,
                    payload: taskMenuItem
                });
                this._showSlideSheet(event);
                this.workflowEditorSubChromeConfig.changeRouterBySelf = false;
            }
        } else if (event.type === NodeEventType.blurNode) {
            this.diagramConfig.nodes = this._diagramService.serialize();
            // add the task to store
            if (event.payload.name) {
                const taskMenuItem = new TaskMenuItem(event.payload.id, event.payload.guid, event.payload.name);
                this._store.dispatch({
                    type: WorkflowActionTypes.UPDATE_CURRENT_WORKFLOW_TASK,
                    payload: taskMenuItem
                });
                this.workflowEditorSubChromeConfig.changeRouterBySelf = false;
            }
        } else if (event.type === NodeEventType.clickNode) {
            if (event.payload.type === START_NODE.type && event.payload.guid === START_NODE.guid) {
                // the built-in workflow do not has triggerSetting
                const definitionSetting = this._definitionSetting || new DefinitionSetting();
                this._workflowConfigService.setDefinitionSettingConfig = _lodash.cloneDeep(definitionSetting);

                const triggerSetting = this._triggerCondition || new WorkflowTriggerSettings();
                this._workflowConfigService.setWorkflowTriggerSettingConfig = _lodash.cloneDeep(triggerSetting);

                this._slideSheetService.showSlideSheetForWorkflowTriggerSettings(event, this._triggerSettingSlideSheet$);
            } else if (TaskTypes.indexOf(event.payload.type) > -1) {
                if (event.payload.guid) {
                    this._showSlideSheet(event);
                }
            }
        } else if (event.type === NodeEventType.addkNode) {
            const id = this._workflowService.getIdFromServerInStore(this._store);
            event.payload.id = id;
            this._diagramService.setNodeId(event.payload.guid, id);
            const availableGatewayTypes = [GatewayType.PARALLEL, GatewayType.CONDITIONAL];
            if (availableGatewayTypes.includes(event.payload.type)) {
                this._activeTask = event.payload;
                // create gateway config and attach to the active node.
                this._createGateway(event.payload.type);
            }

            this.workflowEditorSubChromeConfig.changeRouterBySelf = false;
        } else if (event.type === NodeEventType.clickNoteMeatball) {
            this._activeTask = event.payload.node;
            this._showContextMenu(event.payload.event);
        } else if (event.type === NodeEventType.deleteNodes) {
            event.payload.forEach(nodeId => {
                this._removeFromMapAndStore(nodeId);
            });

            this.workflowEditorSubChromeConfig.changeRouterBySelf = false;
        }
    }

    private _draftSuccessHandler(config: WorkflowConfig): void {
        this.config = config;
        const message = `${this.config.name} successfully saved as draft.`;
        if (this._opType === WorkflowDialogOperations.NEW || this._isImportWorkflow) {
            this._store.dispatch({
                type: SystemActionTypes.ADD_SCREEN,
                payload: config,
            });
        } else {
            this._store.dispatch({
                type: SystemActionTypes.EDIT_SCREEN,
                payload: config,
            });
        }
        this._toast.notify({
            message: message,
            style: NotificationStyles.Success
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_CURRENT_WORKFLOW_TASK,
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_AUTOMATION_NODE,
        });
        this._changeRouter();
    }

    private _errorHandler(error): void {
        this._setSaveButtonLoading(false);
        let message = '';
        if (this._opType === WorkflowDialogOperations.NEW) {
            message = `${this.config.name} could not be created.Please try again.`;
        } else if (this._opType === WorkflowDialogOperations.EDIT) {
            message = `${this.config.name} could not be updated.Please try again.`;
        }
        if (error.message) {
            if (error.message.indexOf('401') > -1 && error.message.indexOf('Unauthorized') > -1) {
                this._router.navigate(['error']);
            } else if (error.message.includes('name had existed') || error.message.includes('already in use. Please rename.')) {
                message = StringLiteralsPipe.translate('general.name_already_exist');
            } else {
                message = error.message;
            }
        }
        this._toast.notify({
            message: message,
            style: NotificationStyles.Error
        });
    }

    /**
     * formats the configuration for saving.
     * connection lines would be created based on the node tree.
     *
     * @private
     * @return {*}
     * @memberof WorkflowConfigComponent
     */
    private _formatConfiguration() {
        const lines = WorkflowConfigUtility.saveAsConnnectionLines(this._diagramService);
        return {
            connectionLine: lines.connectionLine,
            startTask: lines.startTask,
            triggerCondition: this._triggerCondition,
            definitionSetting: this._definitionSetting
        };
    }

    private _formatGateway(isDraft: boolean = false): Array<any> {
        const gateways = [];
        this._gatewayMap.forEach((item, key) => {
            const definition: any = {
                id: key.trim(),
                type: item.type
            };

            const nonNullableProps = ['source', 'target'];
            nonNullableProps.forEach(propName => {
                if (item[propName]) {
                    definition[propName] = String(item[propName]).trim();
                }
            });

            // need to decide if the data has been changed only in edit mode
            if (this._opType === TaskDefinitionOpType.EDIT && !this._isImportWorkflow) {
                // gateway has been changed
                if (isDraft || this._hasChangedGatewaySet.has(key)) {
                    gateways.push(definition);
                }
            } else {
                // push the gateway into array in new/import/draft mode
                gateways.push(definition);
            }
        });
        return gateways;
    }

    private _formatDefinition(isDraft: boolean = false): Array<any> {
        const definitions = [];
        this._taskDefinitionMap.forEach((item, key) => {
            const definition: any = {
                id: key || this._workflowService.getIdFromServerInStore(this._store),
                guid: item.guid,  // key is guid which is from node delement
                name: item.name,
                description: item.description,
                type: item.type,
                assignee: item.assignee,
                stage: isDraft ? item.stage : (item.stage ? item.stage.id : ''),
                stageColor: item.stageColor,
                fields: item.fields.values(),
                postAction: item.postAction,
                template: item.template
            };

            if (item.postNotification) {
                PostNotification.integrityCheck(item.postNotification);
                definition.postNotification = item.postNotification;
            } else {
                definition.postNotification = PostNotification.getNullValue(item.postNotification);
            }

            let fields;
            if (item.type === TaskType.INPUT) {
                const tempData = Array.from(item.fields.values());
                if (tempData.length > 0) {
                    fields = tempData.map(field => ({ id: field.id, name: field.fieldDefinition.name, type: field.fieldDefinition.type }));
                } else {
                    // handle the case which the field was not created.
                    fields = [];
                    item.template.contains.forEach(row => {
                        row.forEach(control => {
                            if (control.fieldDefinitionId) {
                                fields.push({ id: control.fieldDefinitionId || control.id, name: control.label, type: FieldDefinition.parseType(control.type) });
                            } else {
                                control.fieldDefinitionId = this._workflowService.getIdFromServerInStore(this._store);
                                fields.push({ id: control.fieldDefinitionId || control.id, name: control.label, type: FieldDefinition.parseType(control.type) });
                            }
                        });
                    });
                }
            } else if (item.type === TaskType.APPROVAL) {
                if (item.approvalTask && item.approvalTask.type === TaskSubType.SINGLE) {
                    definition.approvalTask = {
                        type: item.approvalTask.type,
                    };
                }
            }
            definition.fields = fields;

            // need to decide if the data has been changed only in edit mode
            if (this._opType === TaskDefinitionOpType.EDIT && !this._isImportWorkflow) {
                // task defination has been changed
                if (isDraft || this._hasChangedTaskDefinitionSet.has(key)) {
                    definitions.push(definition);
                }
            } else {
                // push the definition into array in new/import/draft mode
                definitions.push(definition);
            }
        });
        return definitions;
    }

    /**
     * serve nodes for diagram rendering with the process definition on initialization.
     *
     * @private
     * @param {ProcessDefinition} workflow
     * @return {*}
     * @memberof WorkflowConfigComponent
     */
    private _formatDiagramNodes(workflow: ProcessDefinition) {
        return WorkflowConfigUtility.loadConnectionLines(workflow, this._opType === WorkflowDialogOperations.DRAFT, this._workflowService);
    }

    private _initComponentByImport(): void {
        const processDefinitionConfig = this._workflowConfigService.getProcessDefinitionConfig;
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_CURRENT_WORKFLOW_TASK,
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_AUTOMATION_NODE,
        });
        if (processDefinitionConfig) {
            this._taskDefinitionMap.clear();
            const processDefinition = ProcessDefinition.parse(processDefinitionConfig);
            processDefinition.taskDefinition.forEach(item => this._taskDefinitionMap.set(item.id, item));
            this._defaultTaskDefinition = new Map(this._taskDefinitionMap);
            const stages = new Map();
            const fields = new Map<string, FieldDefinition>();
            const currentFields = new Map();
            const currentFieldBriefs = [];
            const currentAllControls = [];
            const currentTasks = [];
            this._taskDefinitionMap.forEach(item => {
                if (item.stage) {
                    item.stage.color = item.stageColor;
                    stages.set(item.stage.id, item.stage);
                }
                if (item.template && item.template.contains) {
                    item.template.contains.forEach(row => {
                        row.forEach(control => {
                            if (control) {
                                const field = new FieldDefinition();
                                field.id = control.fieldDefinitionId || control.id;
                                field.name = control.label;
                                field.type = FieldDefinition.parseType(control.type);
                                field.description = control.description;
                                if (control.type === ControlType.NUMBER) {
                                    field.configuration = FieldDefinitionConfiguration.parse(control);
                                }
                                fields.set(field.id, field);
                                const ctrlCopy = { taskDefinitionId: item.id };
                                currentAllControls.push(Object.assign(ctrlCopy, control));
                                currentFields.set(control.id, control);
                                const fieldBrief = FieldBrief.parse(control);
                                fieldBrief.taskDefinitionId = item.id;
                                currentFieldBriefs.push(fieldBrief);
                            }
                        });

                    });
                }
                const taskMenuItem = new TaskMenuItem(item.id, item.guid, item.name);
                currentTasks.push(taskMenuItem);
            });
            // set value for triggerCondtion when editing
            if (processDefinition && processDefinition.configuration && processDefinition.configuration.triggerCondition) {
                this._triggerCondition = processDefinition.configuration.triggerCondition;
            }
            // set value for definitionSetting when editing
            if (processDefinition && processDefinition.configuration && processDefinition.configuration.definitionSetting) {
                this._definitionSetting = processDefinition.configuration.definitionSetting;
            }
            // set current workflow tasks
            if (currentTasks && currentTasks.length > 0) {
                this._store.dispatch({
                    type: WorkflowActionTypes.ADD_CURRENT_WORKFLOW_TASK,
                    payload: currentTasks
                });
            }

            if (stages && stages.size > 0) {
                this._store.dispatch({
                    type: WorkflowActionTypes.SET_STAGE,
                    payload: Array.from(stages.values())
                });
            }

            // set current fields
            if (currentFieldBriefs && currentFieldBriefs.length > 0) {
                this._store.dispatch({
                    type: WorkflowActionTypes.SET_CURRENT_WORKFLOW_FIELDS,
                    payload: currentFieldBriefs
                });
            }

            this._store.dispatch({
                type: WorkflowActionTypes.SET_CURRENT_WORKFLOW_ALL_FIELDS,
                payload: currentAllControls
            });

            // add the fields to the store
            if (fields && fields.size > 0) {
                this._store.dispatch({
                    type: WorkflowActionTypes.ADD_ONE_FIELD,
                    payload: Array.from(fields.values()),
                });

                // add the field to the used field store
                this._store.dispatch({
                    type: WorkflowActionTypes.SET_USED_FIELD,
                    payload: fields
                });
            }

            // set gateway map
            if (processDefinition.gatewayDefinition && processDefinition.gatewayDefinition.length > 0) {
                processDefinition.gatewayDefinition.forEach(item => this._gatewayMap.set(item.id, item));
            }
            this._diagramService.destory();
            // draw the digaram
            this.showDiagram = true;
            const nodes = this._formatDiagramNodes(processDefinition);
            this.diagramConfig.nodes = nodes;
            this._addAutomationNodesToStore(nodes);

            // update the name of the importing workflow
            this.config = new WorkflowConfig();
            this.config.name = processDefinition.name;
            this._defaultConfig = _lodash.cloneDeep(this.config);
            this.workflowEditorSubChromeConfig.config = this.config;

            this._workflowConfigService.setProcessDefinitionConfig = null;
        } else {
            this._router.navigate(['error']);
        }
    }

    private _initComponent(): void {
        this._store.pipe(
            select(screensSelector),
            filter(screen => !!screen),
            map(screens => {
                return screens.filter((item) => item.type === businessConstants.screenType.workflow && item.id === this._screenId);
            }),
            switchMap((screen) => {
                if (!screen || screen.length === 0) {
                    if (this._opType === WorkflowDialogOperations.NEW) {
                        // create workflow - get data from workflow dialog
                        return this._store.pipe(select(workflowBriefSelector), take(1));
                    } else {
                        // edit workflow - get data from server
                        return this._screenService.getById(this._screenId).pipe(take(1));
                    }
                }
                return of(screen[0]);
            }),
            switchMap(screen => {
                if (screen) {
                    if (this._opType === WorkflowDialogOperations.NEW) {
                        return of([screen, null]);
                    }
                    if (this._opType === WorkflowDialogOperations.DRAFT) {
                        const fields: Array<FieldDefinition> = this._storeQueriery.queryBySelector(workflowFieldsSelector);
                        screen.configuration.draftConfig.processDefinition.taskDefinition.forEach(element => {
                            const field = new Map();
                            if (Array.isArray(element.fields)) {
                                element.fields.forEach(item => {
                                    const data = new Field();
                                    if (fields) {
                                        const tempData = fields.find(_data => _data.id === item.id);
                                        if (tempData) {
                                            data.id = tempData.id;
                                            data.fieldDefinition = tempData;
                                            field.set(item.id, data);
                                        }
                                    } else {
                                        data.id = item.id;
                                        data.fieldDefinition = item;
                                        field.set(item.id, item);
                                    }
                                });
                            }
                            element.fields = field;
                        });
                        return of([screen, screen.configuration.draftConfig.processDefinition]);
                    }
                    const tempMap: Map<string, ProcessDefinition> = this._storeQueriery.queryBySelector(workflowMapSelector);
                    const tempConfig = tempMap.get(screen.configuration.id);
                    if (!tempConfig) {
                        return this._workflowService.getProcessDefinitionById(screen.configuration.id).pipe(
                            map(res => {
                                if (res) {
                                    const one = ProcessDefinition.parse(res);
                                    this._store.dispatch({
                                        type: WorkflowActionTypes.GET_ONE_PROCESS_DEFINITION_SUCCESS,
                                        payload: one,
                                    });
                                    return [screen, one];
                                } else {
                                    return [screen, null];
                                }
                            }),
                            catchError(err => {
                                return of([screen, null]);
                            }),
                        );
                    } else {
                        return of([screen, tempConfig]);
                    }
                } else {
                    return of([screen, null]);
                }
            }),
            map(([screen, workflow]) => { // init taskdefinition
                this._taskDefinitionMap = new Map();
                if (workflow) {
                    workflow.taskDefinition.forEach(item => this._taskDefinitionMap.set(item.id, item));
                    this._defaultTaskDefinition = new Map(this._taskDefinitionMap);
                    const stages = new Map();
                    const currentFields = new Map();
                    const currentAllControls = [];
                    const currentFieldBriefs = [];
                    const currentTasks = [];
                    this._taskDefinitionMap.forEach(item => {
                        if (item.stage) {
                            item.stage.color = item.stageColor;
                            stages.set(item.stage.id, item.stage);
                        }
                        if (item.template && item.template.contains) {
                            item.template.contains.forEach(row => {
                                row.forEach(control => {
                                    if (control.type === ControlType.SINGLE_ENTITY_DROP_DOWN || control.type === ControlType.MULTI_ENTITY_DROP_DOWN || control.type === ControlType.SINGLE_USER_DROP_DOWN) {
                                        currentFields.set(control.id, control);
                                        const fieldBrief = FieldBrief.parse(control);
                                        fieldBrief.taskDefinitionId = item.id;
                                        currentFieldBriefs.push(fieldBrief);
                                    }
                                    const ctrlCopy = { taskDefinitionId: item.id };
                                    currentAllControls.push(Object.assign(ctrlCopy, control));
                                });
                            });
                        }
                        const taskMenuItem = new TaskMenuItem(item.id, item.guid, item.name);
                        currentTasks.push(taskMenuItem);
                    });
                    // set current workflow tasks
                    if (currentTasks && currentTasks.length > 0) {
                        this._store.dispatch({
                            type: WorkflowActionTypes.ADD_CURRENT_WORKFLOW_TASK,
                            payload: currentTasks
                        });
                    }

                    // set value for triggerCondtion when editing
                    this._triggerCondition = workflow.configuration.triggerCondition;
                    // set trigger setting config
                    const triggerSetting = this._triggerCondition || new WorkflowTriggerSettings();
                    // set workflow trigger setting for setting the correct status for creating sidenote
                    this._workflowConfigService.setWorkflowTriggerSettingConfig = _lodash.cloneDeep(triggerSetting);
                    // set value for definitionSetting
                    this._definitionSetting = workflow.configuration.definitionSetting;
                    this._store.dispatch({
                        type: WorkflowActionTypes.SET_STAGE,
                        payload: Array.from(stages.values())
                    });

                    // set current fields
                    this._store.dispatch({
                        type: WorkflowActionTypes.SET_CURRENT_WORKFLOW_FIELDS,
                        payload: currentFieldBriefs
                    });

                    this._store.dispatch({
                        type: WorkflowActionTypes.SET_CURRENT_WORKFLOW_ALL_FIELDS,
                        payload: currentAllControls
                    });

                    // set gateway map
                    if (workflow.gatewayDefinition && workflow.gatewayDefinition.length > 0) {
                        workflow.gatewayDefinition.forEach(item => this._gatewayMap.set(item.id, item));
                    }
                }
                return [screen, workflow];
            }),
            map(([screen, workflow]) => { // init diagram
                // new workflow, the component do not destory, so need destory the diagram node
                this._diagramService.destory();
                if (screen && workflow) {
                    const nodes = this._formatDiagramNodes(workflow);
                    this.diagramConfig.nodes = nodes;
                    this.diagramConfig.invalidSetting = workflow.invalidSetting;
                    // add automation nodes to store
                    this._addAutomationNodesToStore(nodes);
                } else {
                    this.diagramConfig.nodes = [];
                }
                return screen;
            }),
            map((screen) => {
                if (!screen) {
                    // if the data is null, open the workflow dialog
                    this._workflowDialogService.open(null, null, false);
                } else {
                    this._store.dispatch({
                        type: WorkflowActionTypes.CREATE_WORKFLOW_BRIEF,
                        payload: screen
                    });
                }
                return screen;
            }),
            take(1),
            catchError(err => {
                this._router.navigate(['error']);
                return of('ERROR');
            }),
        ).subscribe();
    }

    private _initContextMenu(): void {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.showItemIcon = false;
        this.contextMenuConfig.items = [
            { id: 0, text: 'Edit' },
            { id: 1, text: 'Delete' },
        ];
        this._destroySubscriptions.push(
            this.contextMenuConfig.events$.subscribe(event => {
                if (event.payload.id === 1) {
                    this._alertWindow.warn('Delete confirmation required',
                        ['Are you sure you want to delete this task?', 'Anything belong to this task will also be deleted.'],
                        'Delete', 'Cancel').pipe(
                            take(1),
                        ).subscribe((result: boolean) => {
                            if (result) {
                                this._deleteNode();
                            }
                        });
                } else if (event.payload.id === 0) {
                    let item: TaskDefinition;
                    let opType: string;
                    if (this._taskDefinitionMap.has(this._activeTask.id)) {
                        item = this._taskDefinitionMap.get(this._activeTask.id);
                        opType = TaskDefinitionOpType.EDIT;
                    } else {
                        item = new TaskDefinition();
                        item.id = this._activeTask.id;
                        item.guid = this._activeTask.guid;
                        item.name = this._activeTask.name;
                        item.type = this._activeTask.type;
                        opType = TaskDefinitionOpType.NEW;
                    }
                    if (!this._activeTask.showInput && this._activeTask.guid) {
                        this._slideSheetService.showSlideSheetForTaskDefinition(null, _lodash.cloneDeep(item), this._taskSlideSheetForTaskDefinitionAction$, this._taskSlideSheetForTaskDefinitionEvent$, opType, this._isImportWorkflow);
                    }
                }
            })
        );
    }

    private _initData(data: WorkflowConfig): void {
        this.config = data;
        this.workflowEditorSubChromeConfig.config = this.config;
        this._setDraftLinkStatus();
        this._setProjectName();
        this._setSaveButtonLoading(false);
        this.diagramConfig.actions$.next({
            type: NodeActionType.refresh
        });
        this.showDiagram = true;
    }

    private _removeFromMapAndStore(id: string) {
        if (!this._taskDefinitionMap.delete(id)) {
            // Unsaved tasks, their GUID will be used as the key in definition map rather than the guid,
            // need to figure out the really id by iterating each item inside the map.
            let abandonedTaskID = null;
            let skipLoop = false;
            this._taskDefinitionMap.forEach((taskDef, key) => {
                if (skipLoop) {
                    return;
                }
                if (taskDef.guid === id) {
                    abandonedTaskID = taskDef.id;
                    skipLoop = true;
                }
            });
            if (abandonedTaskID) {
                this._taskDefinitionMap.delete(abandonedTaskID);
            }
        }
        this._gatewayMap.delete(id);
        // remove task from store
        this._store.dispatch({
            type: WorkflowActionTypes.DELETE_CURRENT_WORKFLOW_TASK,
            payload: {
                id: id
            }
        });
        this.diagramConfig.nodes = this._diagramService.serialize();
    }

    private _resetPageData(): void {
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_STAGE,
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_CURRENT_WORKFLOW_FIELDS,
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_IDS,
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_CURRENT_WORKFLOW_TASK,
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_AUTOMATION_NODE,
        });
        this._diagramService.destory();
        this.diagramConfig.nodes = [];
        this._opType = '';
        this._activeTask = null;
        this._taskDefinitionMap.clear();
    }

    private _saveAsDraft(): void {
        // definit the parameter for draft config
        const taskDefinitions = [];
        const templates = [];
        const gatewayDefinition = [];
        // get definistion
        const data = this._formatDefinition(true);

        data.forEach(item => {
            taskDefinitions.push(item);
            templates.push(item.template);
        });

        const nodes = this._diagramService.serialize();
        nodes.forEach(item => {
            if (GatewayTypes.indexOf(item.type) > -1) {
                gatewayDefinition.push(...item.gatewayConfig);
            }
        });

        taskDefinitions.forEach(item => {
            const tempNode = nodes.find(node => node.id === item.id);
            if (tempNode) {
                item.valid = tempNode.valid;
            }
        });
        const configuration = this._formatConfiguration();
        const draftConfig = {
            processDefinition: {
                gatewayDefinition: gatewayDefinition,
                taskDefinition: taskDefinitions,
                template: templates,
                configuration: {
                    triggerCondition: this._triggerCondition,
                    definitionSetting: this._definitionSetting
                },
                invalidSetting: this.diagramConfig.invalidSetting
            }
        };
        draftConfig.processDefinition.configuration = configuration;
        this.config.configuration.draft = true;
        this.config.configuration.draftConfig = draftConfig;

        if (this.config.id) {
            this._workflowService.updateWorkflowBrief(this.config).pipe(
                take(1),
            ).subscribe((config) => this._draftSuccessHandler(config), error => this._errorHandler(error));
        } else {
            this._workflowService.createWorkflowBrief(this.config).pipe(
                take(1),
            ).subscribe((config) => this._draftSuccessHandler(config), error => this._errorHandler(error));
        }
    }

    private _saveTaskDefinition() {
        const data = [];
        const taskDefinitions = this._formatDefinition(false);
        const gateways = this._formatGateway(false);

        data.push(...taskDefinitions);
        data.push(...gateways);

        if (data.length === 0) {
            this._createWorkflowBrief();
        } else {
            this._createWorkflowBrief(data);
        }
    }

    private _saveWorkflow(): void {
        if (this._isImportWorkflow) {
            this._saveWorkflowLogic();
        } else {
            if (this.config.configuration.id) {
                this._alertWindow.warn('You are publishing a workflow version',
                    ['Are you sure you want to publish a new workflow version, existing projects might be affected based on the changes.'],
                    'Publish', 'Go Back').pipe(
                        take(1),
                    ).subscribe((result: boolean) => {
                        if (result) {
                            this._saveWorkflowLogic();
                        } else {
                            this._setSaveButtonLoading(false);
                        }
                    });
            } else {
                this._saveWorkflowLogic();
            }
        }
    }

    private _saveWorkflowLogic() {
        const nodes = this._diagramService.serialize();
        if (this._validNodeForSave(nodes)) {
            // do not commit task deletion as the task definition must be kept for instances
            // this._deleteTaskDefinitionAndTemplate();
            // save task task definition and template
            this._saveTaskDefinition();
        } else {
            this._setSaveButtonLoading(false);
            this._toast.notify({
                message: 'These are necessary steps to complete before publishing, please have a check.',
                style: NotificationStyles.Error
            });
        }
    }

    private _setColorForStages(index) {
        try {
            index = parseInt(index, 0);
            if (index >= 0) {
                if (index >= this._colors.length) {
                    index = index % this._colors.length;
                }
                const backColor = this._colors[index] + 26;
                const fontColor = this._workflowService.translateHex(this._colors[index]);
                return new TaskColor(backColor, fontColor);
            }
        } catch (e) {
            const backColor = this._colors[0] + 26;
            const fontColor = this._workflowService.translateHex(this._colors[0]);
            return new TaskColor(backColor, fontColor);
        }
    }

    private _setDraftLinkStatus(): void {
        this.workflowEditorSubChromeConfig.events$.next({
            type: WorkflowEditorSubChromeEventType.draftLinkStatus
        });
    }

    private _setProjectName() {
        if (!this._definitionSetting.projectName.name) {
            this._definitionSetting.projectName.name = this.config.name;
        }
    }

    private _setSaveButtonLoading(isLoading: boolean): void {
        this.workflowEditorSubChromeConfig.events$.next({
            type: WorkflowEditorSubChromeEventType.setLoading,
            payload: isLoading
        });
    }

    private _setWorkflowSettingStatus(entityBriefList) {
        const isManual = this._triggerCondition.triggerMethod.manual;
        const isScheduleJob = this._triggerCondition.triggerMethod.scheduleJob;
        const isEmailDeposit = this._triggerCondition.triggerMethod.emailDeposit;
        const isNoteDeposit = this._triggerCondition.triggerMethod.noteDeposit;
        const isEntityDeposit = this._triggerCondition.triggerMethod.entityDeposit;

        if (isManual || isScheduleJob || isEmailDeposit || isNoteDeposit || isEntityDeposit) {
            if (!this._definitionSetting.projectName) {
                this.diagramConfig.invalidSetting = true;
            } else {
                const entityType = this._triggerCondition.multipleEntity.entityType;
                if (entityType) {
                    const entityTypes = this._storeQueriery.queryBySelector(fullEntityTypeSelector);
                    const validEntityType = entityTypes.find(item => item.id === entityType);
                    if (!validEntityType) {
                        this.diagramConfig.invalidSetting = true;
                    }
                }

                const relationship = this._triggerCondition.multipleEntity.relationship;
                if (relationship) {
                    const entityId = this._triggerCondition.multipleEntity.relationship.parentEntity;
                    if (entityId) {
                        if (entityBriefList && entityBriefList.length > 0) {
                            const validEntity = entityBriefList.find(entity => entity.id === entityId);
                            if (!validEntity) {
                                this.diagramConfig.invalidSetting = true;
                            }
                        }
                    }
                    const relationshipType = this._triggerCondition.multipleEntity.relationship.type;
                    if (relationshipType) {
                        const validRelationshipTypes = this._storeQueriery.queryBySelector(relationshipTypeSelector);
                        const validRelationshipType = validRelationshipTypes.find(item => item.id === relationshipType);
                        if (!validRelationshipType) {
                            this.diagramConfig.invalidSetting = true;
                        }
                    }
                }
            }
        } else {
            this.diagramConfig.invalidSetting = true;
        }
    }

    private _showContextMenu(e: MouseEvent): void {
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(e, ''));
    }

    private _showSlideSheet(event): void {
        let item: TaskDefinition;
        this._activeTask = event.payload;
        let opType: string;
        if (this._taskDefinitionMap.has(this._activeTask.id)) {
            item = this._taskDefinitionMap.get(this._activeTask.id);
            item.name = event.payload.name;
            opType = TaskDefinitionOpType.EDIT;
        } else {
            item = new TaskDefinition();
            item.id = this._activeTask.id;
            item.guid = this._activeTask.guid;
            item.name = event.payload.name;
            item.type = event.payload.type;
            opType = TaskDefinitionOpType.NEW;
        }
        if (!event.payload.showInput && event.payload.guid) {
            this._slideSheetService.showSlideSheetForTaskDefinition(null, _lodash.cloneDeep(item), this._taskSlideSheetForTaskDefinitionAction$, this._taskSlideSheetForTaskDefinitionEvent$, opType, this._isImportWorkflow);
        }
    }

    private _successHandler(config: WorkflowConfig): void {
        this._workflowConfigService.setWorkflowTriggerSettingConfig = '';
        this.config = config;
        let message = '';
        if (this._opType === WorkflowDialogOperations.NEW) {
            message = `New Workflow ${this.config.name} successfully created.`;
            this._store.dispatch({
                type: SystemActionTypes.ADD_SCREEN,
                payload: config,
            });
        } else if (this._opType === WorkflowDialogOperations.EDIT || this._opType === WorkflowDialogOperations.DRAFT) {
            message = `${this.config.name} successfully updated.`;
            this._store.dispatch({
                type: SystemActionTypes.EDIT_SCREEN,
                payload: config,
            });
        }
        this._toast.notify({
            message: message,
            style: NotificationStyles.Success
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_ONE_PROCESS_DEFINITION,
            payload: this.config.configuration.id
        });
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_USED_FIELD,
        });
        this._setSaveButtonLoading(false);
        this.workflowEditorSubChromeConfig.changeRouterBySelf = true;
        if (this.config.configuration) {
            this._router.navigate(['workflow/kanban', this.config.id, this.config.configuration.id]);
        }
    }

    private _updatedAutomationNameByEntryType(item, triggerSetting): void {
        // update automation name if the entry type name was changed
        const index = item.name.indexOf('-');
        item.name = item.name.substring(0, index) + ' - ' + triggerSetting.triggerSource[0].objectTypeName;
    }

    /**
     * validates nodes of the given list for saving.
     * @param nodes node list to verify
     * @returns a flag indicating whether current workflow is valid to save.
     */
    private _validNodeForSave(nodes): boolean {
        // input and approval task nodes.
        const taskNodes = nodes.filter(item => TaskTypes.indexOf(item.type) > -1);
        // conditional gateways and parallel gateways.
        const gatewayNodes = nodes.filter(item => GatewayTypes.indexOf(item.type) > -1);
        const validGatewayNodes = (item) => {
            // the branches list or parent nodes list.
            const conditions = [];
            item.children.forEach(element => {
                const node = this._diagramService.getNode(element);
                conditions.push(node.condition);
            });
            const gatewayBeginTypes = [];
            const gatewayEndTypes = [];
            Object.keys(DefaultGatewayInfo).forEach(dgt => {
                if (DefaultGatewayInfo[dgt].length > 1) {
                    gatewayEndTypes.push(DefaultGatewayInfo[dgt][1].type);
                    gatewayBeginTypes.push(DefaultGatewayInfo[dgt][0].type);
                }
            });

            if (item.type === GatewayType.CONDITIONAL) {
                // for conditional gateways, there's at least one task node in one of its branches.
                return item.gatewayConfig && item.gatewayConfig.length === 2 &&
                    item.gatewayConfig.some(data => gatewayBeginTypes.includes(data.type)) &&
                    item.gatewayConfig.some(data => gatewayEndTypes.includes(data.type)) &&
                    // either a task node or an automation config is found on one of its branches
                    item.branchs.some((data: DiagramBranch) => (conditions.indexOf(data.guid) >= 0 || (data.automationConfigs && data.automationConfigs.length > 0)));
            } else {
                // for parallel gateways, every branch must has at least one node
                return item.gatewayConfig && item.gatewayConfig.length === 2 &&
                    item.gatewayConfig.some(data => gatewayBeginTypes.includes(data.type)) &&
                    item.gatewayConfig.some(data => gatewayEndTypes.includes(data.type)) &&
                    item.branchs.length <= item.children.length && item.branchs.every(data => conditions.indexOf(data.guid) !== -1);
            }
        };
        return nodes.length > 0 && taskNodes.every(item => !!item.valid) && gatewayNodes.every(validGatewayNodes) && !this.diagramConfig.invalidSetting;
    }

    private _validImportWorkflowLogic(): Observable<any> {
        const _valid: Map<string, boolean> = new Map();

        // get all entity ids which are used in task setting
        const entityIds = [];
        this._taskDefinitionMap.forEach((taskDefinition: TaskDefinition, key: string) => {
            if (taskDefinition.postAction && taskDefinition.postAction.relationship) {
                const relationship = taskDefinition.postAction.relationship;
                if (relationship.child) {
                    entityIds.push(relationship.child);
                }
            }
        });
        if (this._triggerCondition && this._triggerCondition.multipleEntity && this._triggerCondition.multipleEntity.relationship && this._triggerCondition.multipleEntity.relationship.parentEntity) {
            entityIds.push(this._triggerCondition.multipleEntity.relationship.parentEntity);
        }

        // send one request to get all valid/invalid entity
        return this._entityService.getEntityListByIds(entityIds).pipe(
            take(1),
            map((response) => {
                // map the return entities to brief entities
                const entityBriefList = this._entityService.mapEntityBriefList(response);
                // handle workflow setting status
                this._setWorkflowSettingStatus(entityBriefList);
                // verify all task definition
                this._taskDefinitionMap.forEach((taskDefinition: TaskDefinition, key: string) => {
                    // valid the basic setting information for import workflow
                    let _validBasicSetting = true;
                    _validBasicSetting = this._validImportBasicSetting(taskDefinition, key);
                    // valid the form builder information for import workflow
                    let _validFormBuilder = true;
                    _validFormBuilder = this._validImportFormBuilder(taskDefinition, key);
                    // valid the post actions information for import workflow
                    let _validPostActions = true;
                    _validPostActions = this._validImportPostActions(taskDefinition, key, entityBriefList);

                    // save the each task definistion status to valid map
                    _valid.set(key, _validBasicSetting && _validFormBuilder && _validPostActions);
                });

                return Array.from(_valid.values()).every(item => !!item);
            }));
    }

    // valid the basic setting information for import workflow
    private _validImportBasicSetting(taskDefinition: TaskDefinition, key: string): boolean {
        let _valid = true;
        // verify task definistion name
        if (!taskDefinition.name) {
            _valid = false;
        }
        // verify task definistion stage
        if (!taskDefinition.stage.id) {
            _valid = false;
        }
        // verify task definistion assignee
        if (taskDefinition.assignee) {
            const listItems = this._storeQueriery.queryBySelector(usersAndTeamsSelector);
            const assignee = _lodash.cloneDeep(taskDefinition.assignee);
            // remove invalid users
            if (assignee.users) {
                const invalidUsers = [];
                assignee.users.split(',').forEach(item => {
                    const index = listItems.findIndex(_assignee => _assignee.id === item);
                    if (index <= -1) {
                        _valid = false;
                        invalidUsers.push(item);
                    }
                });
                if (invalidUsers.length > 0) {
                    const users = taskDefinition.assignee.users.split(',');
                    taskDefinition.assignee.users = users.filter(function (item) {
                        return invalidUsers.indexOf(item) < 0;
                    }).join(',');
                }

            }
            // remove invalid teams
            if (assignee.teams) {
                const invalidTeams = [];
                assignee.teams.split(',').forEach(item => {
                    const index = listItems.findIndex(_assignee => _assignee.id === item);
                    if (index <= -1) {
                        _valid = false;
                        invalidTeams.push(item);
                    }
                });
                if (invalidTeams.length > 0) {
                    const teams = taskDefinition.assignee.teams.split(',');
                    taskDefinition.assignee.teams = teams.filter(function (item) {
                        return invalidTeams.indexOf(item) < 0;
                    }).join(',');
                }
            }
        } else {
            _valid = false;
        }
        // update node valid property
        if (!_valid) {
            this._diagramService.updateNode(key, { valid: false });
        }
        return _valid;
    }

    // valid the form builder information for import workflow
    private _validImportFormBuilder(taskDefinition: TaskDefinition, key: string): boolean {
        let _valid = true;

        if (taskDefinition.type === TaskType.INPUT && taskDefinition.template) {
            taskDefinition.template.contains.forEach(row => {
                row.forEach(control => {
                    // TODO： will be changed， Need to cover all control type in form builder later.
                    if (control.type === ControlType.TEXT ||
                        control.type === ControlType.NUMBER ||
                        control.type === ControlType.FILE ||
                        control.type === ControlType.DATE) { // only need to verify control label
                        if (!control.label) {
                            _valid = false;
                        }
                    } else if (control.type === ControlType.SINGLE_TEXT_DROP_DOWN) {
                        if (!control.label) {
                            _valid = false;
                        }
                        if (control.source) {
                            if (control.source.length === 0) {
                                _valid = false;
                            }
                        } else {
                            _valid = false;
                        }
                    } else if (control.type === ControlType.SINGLE_ENTITY_DROP_DOWN ||
                        control.type === ControlType.MULTI_ENTITY_DROP_DOWN ||
                        control.type === ControlType.SINGLE_USER_DROP_DOWN) { // entity related controls
                        if (!control.label) {
                            _valid = false;
                        }
                        if (control.source) {
                            const listItems = this._storeQueriery.queryBySelector(fullEntityTypeSelector);
                            if (control.source.length > 0) {
                                if (control.source.length === 1 && control.source[0] === 'all') {
                                    // no need to handle this case.
                                } else {
                                    const invalidEntityType = [];
                                    control.source.forEach(item => {
                                        const index = listItems.findIndex(entityType => entityType.id === item);
                                        if (index <= -1) {
                                            _valid = false;
                                            invalidEntityType.push(item);
                                        }
                                    });
                                    if (invalidEntityType.length > 0) {
                                        control.source = control.source.filter(function (item) {
                                            return invalidEntityType.indexOf(item) < 0;
                                        });
                                    }
                                }
                            } else {
                                _valid = false;
                            }
                        } else {
                            _valid = false;
                        }
                    }
                });
            });
            // update the node property
            if (!_valid) {
                this._diagramService.updateNode(key, { valid: false });
            }
        }
        return _valid;
    }

    // valid the post action information for import workflow
    private _validImportPostActions(taskDefinition: TaskDefinition, key: string, entityBriefList): boolean {
        let _valid = true;
        // exist post action
        if (taskDefinition.postAction && taskDefinition.postAction.relationship) {
            const relationship = taskDefinition.postAction.relationship;
            const relationshipType = relationship.type;
            // verify relationship type
            if (relationshipType) {
                const validRelationshipTypes = this._storeQueriery.queryBySelector(relationshipTypeSelector);
                const validRelationshipType = validRelationshipTypes.find(item => item.id === relationshipType);
                if (!validRelationshipType) {
                    _valid = false;
                    taskDefinition.postAction.relationship.type = '';
                }
            }
            // verify child entity
            const childEntityId = relationship.child;
            if (childEntityId) {
                if (entityBriefList && entityBriefList.length > 0) {
                    const validEntity = entityBriefList.find(entity => entity.id === childEntityId);
                    if (!validEntity) {
                        _valid = false;
                        taskDefinition.postAction.relationship.child = '';
                    }
                } else {
                    _valid = false;
                    taskDefinition.postAction.relationship.child = '';
                }
            }
        } else {
            _valid = false;
        }
        // update the node property
        if (!_valid) {
            this._diagramService.updateNode(key, { valid: false });
        }
        return _valid;
    }

    private _validTaskPostActionsByTemplateEntityFieldsChange() {
        // get current workflow entity related fields
        const currentWorkflowEntityControls = this._storeQueriery.queryBySelector(currentWorkflowFieldsSelector);
        this._taskDefinitionMap.forEach(item => {
            if (item.postAction.relationship.type) {
                if (item.postAction.relationship.parent.controlId) {
                    if (currentWorkflowEntityControls && currentWorkflowEntityControls.length > 0) {
                        if (!currentWorkflowEntityControls.find(control => control.controlId === item.postAction.relationship.parent.controlId)) {
                            item.postAction.relationship.parent = new Parent();
                            this._diagramService.updateNode(item.id, { valid: false });
                        }
                    } else {
                        item.postAction.relationship.parent = new Parent();
                        this._diagramService.updateNode(item.id, { valid: false });
                    }
                } else {
                    item.postAction.relationship.parent = new Parent();
                    this._diagramService.updateNode(item.id, { valid: false });
                }
            }
        });
    }

    private _workflowEditorSubChromeHandlers(action): void {
        switch (action.type) {
            case WorkflowEditorSubChromeActionType.onSave:
                this._saveWorkflow();
                break;
            case WorkflowEditorSubChromeActionType.resetPage:
                this._resetPageData();
                break;
            case WorkflowEditorSubChromeActionType.onSaveAsDraft:
                this._saveAsDraft();
                break;
            case WorkflowEditorSubChromeActionType.onClickDraftLink:
                this._changeRouter();
                break;
            case WorkflowEditorSubChromeActionType.syncNodes:
                this.diagramConfig.nodes = this._diagramService.serialize();
                break;
            default:
                break;
        }
    }
}
